import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';

export default function DoctorWebsite() {
  const [currentPage, setCurrentPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  const NavigationBar = () => (
    <nav className="bg-white shadow-sm fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <h1 className="text-xl font-semibold text-emerald-800">Dr. Irena Vigderovich</h1>
          </div>
          
          <div className="hidden md:flex space-x-8">
            <button
              onClick={() => setCurrentPage('home')}
              className={`${
                currentPage === 'home' ? 'text-emerald-700 border-b-2 border-emerald-700' : 'text-gray-600 hover:text-emerald-700'
              } px-3 py-2 text-sm font-medium transition-colors`}
            >
              Home
            </button>
            <button
              onClick={() => setCurrentPage('privacy')}
              className={`${
                currentPage === 'privacy' ? 'text-emerald-700 border-b-2 border-emerald-700' : 'text-gray-600 hover:text-emerald-700'
              } px-3 py-2 text-sm font-medium transition-colors`}
            >
              Privacy Statement
            </button>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-gray-600 hover:text-emerald-700"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button
              onClick={() => {
                setCurrentPage('home');
                setMenuOpen(false);
              }}
              className={`${
                currentPage === 'home' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-emerald-50'
              } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
            >
              Home
            </button>
            <button
              onClick={() => {
                setCurrentPage('privacy');
                setMenuOpen(false);
              }}
              className={`${
                currentPage === 'privacy' ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-emerald-50'
              } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
            >
              Privacy Statement
            </button>
          </div>
        </div>
      )}
    </nav>
  );

  const HomePage = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-96 h-[28rem] rounded-lg shadow-xl overflow-hidden">
                <img 
                  src={process.env.PUBLIC_URL + '/irena_photo.jpg'} 
                  alt="Dr. Irena Vigderovich"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>


          <div className="space-y-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-serif text-emerald-800 mb-2">Dr Irena Vigderovich</h1>
              <p className="text-xl text-emerald-700 font-light">General Practitioner</p>
            </div>

            <div className="prose prose-emerald max-w-none">
              <p className="text-gray-700 leading-relaxed">
                Dr. Irena Vigderovich is a Melbourne University graduate with extensive experience in women's health. 
                She has a special interest in and training in treating menopause, contraceptive care including long-acting reversible 
                contraceptives (Mirena, Kyleena, and Implanon), Pregnancy shared care, and ear, nose, and throat 
                disorders (trained at RVEEH).
              </p>
              <br></br>
              <p className="text-gray-700 leading-relaxed">
                Dr. Vigderovich is also skilled in cosmetic anti-wrinkle injections, minor skin procedures, tongue tie 
                treatments, injection of keloid scars, treatment of hyperhidrosis (excessive sweating), hair loss treatment, and iron infusions, providing comprehensive, patient-centered care across all stages of 
                women's health. Fluent in Russian, she welcomes patients from diverse backgrounds and is passionate 
                about empowering women through informed health choices.
              </p>
              <br></br>
              <p className="text-gray-700 leading-relaxed">
                Outside of work, Irena has two grown-up children and enjoys Pilates and walking her dog in her free time.
              </p>
            </div>

            <div className="pt-4">
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">Qualifications</h3>
              <p className="text-gray-700 italic">MBBS, FRACGP</p>
            </div>

            <div className="pt-6">
              <a
                href="https://www.hotdoc.com.au/medical-centres/brighton-east-VIC-3187/brighton-family-women-s-clinic/doctors/dr-irena-vigderovich-1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Book an Appointment
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  
  const PrivacyPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-serif text-emerald-800 mb-6 text-center">Privacy Statement</h1>
          <h2 className="text-2xl text-emerald-700 mb-4 text-center">Dr. Irena Vigderovich Privacy Policy</h2>
          <br></br>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              The objective of this privacy notice is to provide you, the patient, with clear information on how 
              your personal information is collected and used within my practice. Occasionally I also need to share 
              your personal information to involve others in your healthcare and this policy outlines when, how, and 
              why I share your information.
            </p>
            <br></br>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">1. Who can I contact about this policy?</h3>
                <p>
                  For enquiries concerning this policy, you can contact Dr. Irena Vigderovich directly at the practice.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">2. When and why is your consent necessary?</h3>
                <p>
                  When you register as a patient, you provide consent for the GPs and practice staff to access and use 
                  your personal information to facilitate the delivery of healthcare. Access to your personal information 
                  is restricted to GPs and practice team members who require it for your care. If we ever use your 
                  personal information for purposes other than healthcare provision, we will obtain additional consent 
                  from you.
                </p>
              
                <p className="mt-2">
                  It is important to us that as my patient, you understand why we collect and use your personal 
                  information.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">3. Why do I collect, use, store, and share your personal information?</h3>
                <p>
                  My practice collects, uses, stores, and shares your personal information primarily to manage your 
                  health safely and effectively. This includes providing healthcare services, managing medical records, 
                  coordinating with other healthcare providers involved in your care, billing and administrative 
                  purposes, and complying with legal obligations.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">4. What personal information do I collect?</h3>
                <p>
                  The personal information collected may include your name, address, contact details, date of birth, 
                  medical history, test results, medications, allergies, and any other information relevant to your 
                  healthcare.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">5. Who do I share your personal information with?</h3>
                <p>
                  Your personal information may be shared with other healthcare providers involved in your care 
                  (such as specialists, hospitals, or allied health professionals), pathology and radiology services, 
                  pharmacies, Medicare and health insurers for billing purposes, and other parties as required by law.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">6. How do I protect your personal information?</h3>
                <p>
                  I take reasonable steps to protect your personal information from misuse, interference, loss, 
                  unauthorized access, modification, or disclosure. This includes secure storage systems, password 
                  protection, and restricted access to personal information.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">7. How can you access and correct your personal information?</h3>
                <p>
                  You have the right to access and request correction of your personal information. Please contact 
                  the practice if you wish to access your medical records or believe any information held is incorrect.
                </p>
                <br></br>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">8. How can you make a complaint?</h3>
                <p>
                  If you have concerns about how your personal information has been handled, please contact the practice 
                  directly. We will investigate your complaint and respond to you. If you are not satisfied with our 
                  response, you may contact the Office of the Australian Information Commissioner.
                </p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                This privacy policy is reviewed regularly and updated as necessary to ensure compliance with current 
                privacy legislation and best practice standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <NavigationBar />
      <div className="pt-16">
        {currentPage === 'home' ? <HomePage /> : <PrivacyPage />}
      </div>
    </div>
  );
}